/**
 * @flow
 * Created by neo on 05.03.17.
 */
import mobx, {observable, action, computed} from 'mobx';
import { FlightOfferAircraft } from '../FlightOffer';
import Payment from '../Payment';
import BackendFactory from '../../Services/BackendFactory';
import Address from '../Address';
import Voucher from '../Voucher';
import Settings from '../Settings';
import PromoCode from '../PromoCode';
import AbstractBookingRequest from './AbstractBookingRequest';

const moment = require('moment');

export default class VoucherBookingRequest {
  @observable first_name: string;
  @observable last_name: string;
  @observable email: string;
  @observable payment: Payment = new Payment();
  @observable address: Address = new Address();
  @observable value: numeric = 10;
  @observable currency_code: string;
  @observable note: string;
  @observable billing_remarks: string = "";
  @observable promo_code: PromoCode = new PromoCode();
  @observable price_explanation: Array<Object>;
  @observable offer_booking: AbstractBookingRequest;

  constructor(json?: any) {
    if(json) {
      this.first_name = json.first_name;
      this.last_name = json.last_name;
      this.email = json.email;
      this.payment = new Payment(json.payment || (this.payment_types.length ? this.payment_types[0] : {}));
      this.address = new Address(json.address);
      this.currency_code = json.currency_code || Settings.settings.get('currency').code;
      this.value = json.value || 10;
      this.note = json.note;
      this.billing_remarks = json.billing_remarks || "";
      this.promo_code = new PromoCode(json.promo_code);
    } else {
      this.payment = new Payment(this.payment_types.length ? this.payment_types[0] : {});
      this.currency_code = Settings.settings.get('currency').code;
    }

    // get promo code from local storage if present
    if (!this.promo_code.code) {
      const key = AbstractBookingRequest.getPromoCodeStorageKey();
      if (localStorage && localStorage.getItem(key)) {
        this.promo_code.code = localStorage.getItem(key);
      }
    }
  }

  @computed get payment_types(): Array {
      return [Settings.enums.get('payment_types').ONLINE];
  }

  @computed get actualPrice(): number {
    if (this.offer_booking) {
      return this.offer_booking.actualPrice;
    }

    return +this.value;
  }

  @computed get price(): number {
    return +this.value;
  }

  @computed get currency(): string {
    return this.offer_booking && this.offer_booking.aircraft ? this.offer_booking.aircraft.currency_code : this.currency_code;
  }

  @computed get currencyModel(): Object {
    return this.offer_booking && this.offer_booking.aircraft ? this.offer_booking.aircraft.currency : Settings.settings.get('currency');
  }

  @computed get currencyRate(): number {
    if (!this.payment.currency || !this.currencyModel) {
      return 1;
    }

    return this.payment.currency.conversion_rate_eur / this.currencyModel.conversion_rate_eur;
  }

  @computed get paymentMethodCommission(): number {
    if (!this.payment.fee_fixed && !this.payment.fee_percent) {
      return 0;
    }

    const fixedFee = this.payment.fee_fixed ? this.currencyRate * this.payment.fee_fixed : 0;
    const commission = Math.round((this.actualPrice * (this.payment.fee_percent / 100) + fixedFee) * 100) / 100;
    return Math.ceil(+this.actualPrice + commission) - this.actualPrice;
  }

  @computed get validEmail(): boolean {
    var re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(this.email);
  }

  @computed get validValue(): boolean {
    return !isNaN(this.value * 1) && this.value % 5 === 0 && this.value > 0 || this.offer_booking;
  }

  @computed get validClient(): boolean {
    return this.first_name && this.last_name && this.validEmail && this.validValue;
  }

  @computed get valid(): boolean {
    return this.validClient && this.validValue && this.address.valid;
  }

  @computed get invalid(): boolean {
    return !this.valid;
  }

  @action async send(): Promise<?string> {
    const backend = await BackendFactory();
    const res = await backend.post('/voucher', mobx.toJS(this));

    if(res.ok) {
      return res.data;
    }

    return null;
  }

  @action resetPayment(): void {
    if (this.payment_types.length > 0) {
      this.payment = new Payment(this.payment_types[0]);
    } else {
      this.payment = new Payment();
    }
  }

  @action removePromoCode(): void {
    this.price_explanation = [];
    this.promo_code = new PromoCode();

    if (this.offer_booking) {
      this.offer_booking.removePromoCode();
      this.value = this.offer_booking.actualPriceWithoutDiscount;
    }

    // reset payment to default if it was cleared
    if (!this.payment.id) {
      this.resetPayment();
    }
  }

  @action async applyDiscounts(): Promise<?string> {
    if (this.offer_booking) {
      this.offer_booking.promo_code = this.promo_code;

      await this.offer_booking.applyDiscounts();

      this.value = this.offer_booking.actualPrice;
      this.promo_code = this.offer_booking.promo_code;
      this.price_explanation = this.offer_booking.price_explanation;
    }
  }

  @action getPriceToPay(): number {
    return (parseInt((+this.actualPrice + this.paymentMethodCommission) * 100) / 100);
  }

  @action updateFirstName = (name: string) => {
    this.first_name = name;
  };

  @action updateLastName = (name: string) => {
    this.last_name = name;
  };
}



// WEBPACK FOOTER //
// ./src/App/Store/Booking/VoucherBookingRequest.js